import { Button, FormControl, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import AudienceSelector from './AudienceSelector';
import { v4 as uuidv4 } from 'uuid';
import { useParams } from 'react-router-dom';
import { MessageDocument, useFirebaseService } from '../../../data/FirebaseService';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { FirebaseConfig } from '../../../data/firebaseConfig';
import SendingAnimation from './SendingAnimation';

const CreateMessagePage: React.FC = () => {
    const [isSending, setIsSending] = useState(false);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [audience, setAudience] = useState<string[]>([]);
    const [duration, setDuration] = useState(30);
    const [notificationType, setNotificationType] = useState<string>('popup');
    const [errors, setErrors] = useState({
        title: "",
        description: "",
        audience: "",
        duration: "",
        notificationType: "",
    });

    const { id: messageId } = useParams<{ id: string }>();
    const isEditing = !!messageId;
    const navigate = useNavigate();
    const firebaseService = useFirebaseService()

    const functions = getFunctions(FirebaseConfig.app);
    const sendMessage = httpsCallable(functions, "sendMessage");

    const handleCancel = () => {
        navigate('/messages');
    };

    // const handleSaveDraft = () => {
    //     // Logic to save draft
    //     console.log('Draft saved:', { title, description });
    // };

    const handleSend = () => {
        console.log('Message sent:', { title, description });

        const body = {
            externalId: uuidv4(),
            title: title,
            body: description,
            duration: duration,
            audience: audience,
            notificationType: notificationType,
        };

        setIsSending(true);
        sendMessage(body)
            .then(response => {
                console.log('Message sent successfully:', response.data);
                setIsSending(false);
                toast.success('Message sent successfully!', { position: 'bottom-right', autoClose: 1000 });
                navigate('/messages');
            })
            .catch(error => {
                console.error('Error sending message:', error);
                toast.error('Failed to send message. Please try again.', { position: 'bottom-right' });
                setIsSending(false);
            });
    };

    const validateFields = () => {
        const newErrors = {
            title: title.trim() ? "" : "Title is required",
            description: description.trim() ? "" : "Description is required",
            audience: audience.length > 0 ? "" : "At least one group must be selected",
            duration: duration ? "" : "Duration is required",
            notificationType: notificationType ? "" : "Notification Type is required",
        };
        setErrors(newErrors);
        return Object.values(newErrors).every((error) => error === "");
    };

    const handleFormSubmit = () => {
        if (validateFields()) {
            handleSend();
        }
    };

    useEffect(() => {
        if (isEditing) {
            // Fetch message by id
            console.log('Fetching message by id:', messageId);

            if (firebaseService) {
                firebaseService.getDocument<MessageDocument>('messages', messageId).then((doc) => {
                    console.log('Fetched message:', doc);
                    if (doc) {
                        setTitle(doc.title);
                        setDescription(doc.body);
                        setDuration(doc.duration);
                        setAudience(doc.audience);
                        setNotificationType(doc.notificationType);
                    }
                });
            }
        }
    }, [firebaseService, isEditing, messageId]);

    return (
        <div style={{ position: 'relative' }}>
            <SendingAnimation isSending={isSending} />
            <Typography variant="h5" component="h2" className='page-title'>
                {isEditing ? 'Edit Message' : 'Create New Message'}
            </Typography>
            <form noValidate>
                <div style={{ marginBottom: '20px' }}>
                    <TextField
                        variant="outlined"
                        label="Title"
                        fullWidth
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        disabled={isSending}
                        error={!!errors.title}
                        helperText={errors.title}
                    />
                </div>
                <div style={{ marginBottom: '20px' }}>
                    <label>Description</label>
                    <ReactQuill style={{ paddingTop: '10px', height: '200px' }} theme="snow" value={description} onChange={setDescription} readOnly={isSending} />
                    {errors.description && (
                        <Typography style={{ marginLeft: '10px', marginTop: '50px' }} color="error" variant="body2">{errors.description}</Typography>
                    )}
                </div>

                <div style={{ marginTop: '50px', marginBottom: '20px' }}>
                    <AudienceSelector initialSelection={audience} onSelectionChange={setAudience} />
                    {errors.audience && (
                        <Typography style={{ marginLeft: '10px', marginTop: '10px' }} color="error" variant="body2">{errors.audience}</Typography>
                    )}
                </div>

                <div style={{ marginBottom: '20px' }}>
                    <FormControl fullWidth variant="outlined" disabled={isSending}>
                        <InputLabel id="notification-type-label">Notification Type</InputLabel>
                        <Select
                            labelId="notification-type"
                            value={notificationType}
                            onChange={(e) => setNotificationType(e.target.value)}
                            label="Notification Type"
                        >
                            <MenuItem value='popup'>Pop-up</MenuItem>
                            <MenuItem value='window'>Centered Window</MenuItem>
                            <MenuItem value='fullscreen'>Full-screen</MenuItem>

                        </Select>
                        {errors.notificationType && <Typography color="error" variant="body2">{errors.notificationType}</Typography>}
                    </FormControl>
                </div>
                <div style={{ marginBottom: '20px' }}>
                    <FormControl fullWidth variant="outlined" disabled={isSending}>
                        <InputLabel id="duration-label">Duration</InputLabel>
                        <Select
                            labelId="duration-label"
                            value={duration}
                            onChange={(e) => setDuration(e.target.value as number)}
                            label="Duration"
                        >
                            <MenuItem value={15}>15 mins</MenuItem>
                            <MenuItem value={30}>30 mins</MenuItem>
                            <MenuItem value={60}>1 hour</MenuItem>
                            <MenuItem value={120}>2 hours</MenuItem>
                            <MenuItem value={300}>5 hours</MenuItem>
                            <MenuItem value={1440}>1 day</MenuItem>
                            <MenuItem value={10080}>1 week</MenuItem>
                        </Select>
                        {errors.duration && <Typography color="error" variant="body2">{errors.duration}</Typography>}
                    </FormControl>
                </div>
                <div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
                    <Button variant="text" color="secondary" onClick={handleCancel} disabled={isSending}>Cancel</Button>
                    {/* <Button variant="contained" color="primary" onClick={handleSaveDraft} disabled={isSending}>Save Draft</Button> */}
                    <Button variant="contained" color="primary" onClick={handleFormSubmit} disabled={isSending}>Send</Button>
                </div>
            </form>
        </div >

    );
};

export default CreateMessagePage;